import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import {
  STATUS_DONE,
  STATUS_ACTIVE,
  STATUS_PENDING,
  type Dispatch,
} from "@/lib/types";

/** ESC's known pseudo-customer numbers for non-billable time blocks. */
export const PSEUDO_CUSTOMERS = new Set([
  "0079253", // Off Today
  "0079690", // Pick Up Part
  "0036012", // Office / training / other
]);

/** Check whether a dispatch is a pseudo (non-customer) entry. */
export function isPseudoDispatch(row: Dispatch): boolean {
  if (PSEUDO_CUSTOMERS.has(row.custno)) return true;
  if (row.window_code === "OFF") return true;
  return false;
}

/**
 * Classify a pseudo-dispatch by its purpose so we can show a type icon.
 * Inferred from customer name / window code since ESC doesn't have a
 * structured "kind of holding ticket" field.
 *
 * Eventually replaced by first-class tech_availability + parts_pickup
 * tables (tasks #48 and #49) - this is the transition-period heuristic.
 */
export type PseudoKind =
  | "off"
  | "parts"
  | "office"
  | "meeting"
  | "training"
  | "other";

export function detectPseudoKind(row: Dispatch): PseudoKind | null {
  if (!isPseudoDispatch(row)) return null;
  const name = (row.customer_fullname ?? row.locname ?? "").toLowerCase();
  const win = (row.window_code ?? "").toUpperCase();

  // Order matters - check most-specific first because "office" contains "off"
  // and we don't want "office" tickets miscategorized as "off".
  if (name.includes("office")) return "office";
  if (name.includes("part")) return "parts";
  if (name.includes("training") || name.includes("class")) return "training";
  if (name.includes("meeting")) return "meeting";
  if (win === "OFF" || name.includes("off today") || /^off\b/.test(name))
    return "off";
  return "other";
}

// Time band for route optimization (ROUTE-04a). Band by the time WINDOW's start
// hour (e.g. 8-12 → 8, 10-2 → 10, 12-4 → 12, 3-7 → 15), with after-hours forced
// last. Stops sharing a window land in the same band and CAN be reordered for a
// shorter drive; different windows stay in order. (Banding by exact promised
// hour was too strict — it locked every stop to its time so nothing optimized.)
export function timeBand(r: Dispatch): number {
  const win = (r.window_code ?? "").toUpperCase().trim();
  if (win.includes("AFTER")) return 100; // after-hours always last
  const w = /^(\d{1,2})/.exec(win);
  if (w) {
    let h = parseInt(w[1], 10);
    if (h >= 1 && h <= 7) h += 12; // afternoon windows (1-7 → 13-19)
    return h;
  }
  // No time window (special codes / blank) → promised hour, else flexible.
  const t = /^(\d{2}):/.exec(r.tpromtime ?? "");
  if (t) return parseInt(t[1], 10);
  return 50;
}

/**
 * Fetch dispatches for a specific date.
 * Returns all stops for all dept-03 techs on that date.
 */
export function useBoardDispatches(dateISO: string) {
  return useQuery({
    queryKey: ["board_dispatches", dateISO],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_route_data")
        .select("*")
        .eq("appointment_date", dateISO)
        .order("tpromtime", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as Dispatch[];
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export interface ActiveTech {
  empno: string;
  empname: string | null;
  firstname: string | null;
  lastname: string | null;
  dept: string | null;
}

/**
 * Fetch all currently-active techs in a given department.
 * Used to show empty lanes for techs with no dispatches today (so the
 * dispatcher can drag-drop new work onto them).
 *
 * Excludes pseudo-employee numbers (0000, 0001, 7777, 8888, 9999) that
 * ESC uses for non-tech columns like "Parts" or "Unassigned".
 */
const PSEUDO_EMPNOS = new Set(["0000", "0001", "7777", "8888", "9999"]);

export function useActiveTechs(dept: string = "03") {
  return useQuery({
    queryKey: ["active_techs", dept],
    queryFn: async (): Promise<ActiveTech[]> => {
      const { data, error } = await supabase
        .from("mirror_employee")
        .select("empno, empname, firstname, lastname, dept, inactive")
        .eq("dept", dept)
        .eq("inactive", false);
      if (error) throw error;
      return ((data ?? []) as ActiveTech[]).filter(
        (t) => !PSEUDO_EMPNOS.has(t.empno),
      );
    },
    staleTime: 5 * 60 * 1000, // techs don't change often
  });
}

/** All active (non-pseudo) employees across departments — for surfaces that aren't
 * dept-scoped, e.g. logging install labor where the installer may not be in dept 03. */
export function useAllActiveTechs() {
  return useQuery({
    queryKey: ["active_techs_all"],
    queryFn: async (): Promise<ActiveTech[]> => {
      const { data, error } = await supabase
        .from("mirror_employee")
        .select("empno, empname, firstname, lastname, dept, inactive")
        .eq("inactive", false);
      if (error) throw error;
      return ((data ?? []) as ActiveTech[]).filter((t) => !PSEUDO_EMPNOS.has(t.empno));
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** empno → hourly cost rate. Pegasus pay row (tech_pay.base_rate, hourly) wins;
 * falls back to the ESC-mirrored rate (mirror_employee.pay_rate). Used to auto-fill
 * install labor cost so the office never re-keys a tech's rate. */
export function useTechRates() {
  return useQuery({
    queryKey: ["tech_rates"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Map<string, number>> => {
      const [{ data: emps }, { data: pays }] = await Promise.all([
        supabase.from("mirror_employee").select("empno, pay_rate").eq("inactive", false),
        supabase.from("tech_pay").select("empno, base_rate, pay_type"),
      ]);
      const m = new Map<string, number>();
      for (const e of (emps ?? []) as { empno: string; pay_rate: number | null }[]) {
        if (e.pay_rate != null) m.set(e.empno, Number(e.pay_rate));
      }
      for (const p of (pays ?? []) as { empno: string; base_rate: number | null; pay_type: string | null }[]) {
        // 0 = never filled in (the drawer used to save a blank as 0) — keep ESC's rate.
        if (p.base_rate != null && Number(p.base_rate) > 0 && (p.pay_type ?? "hourly") === "hourly") m.set(p.empno, Number(p.base_rate));
      }
      return m;
    },
  });
}

export interface TechLane {
  empno: string;
  name: string;
  stops: Dispatch[];
  totalStops: number; // customer-facing only (excludes pseudos)
  doneCount: number;
  activeStop: Dispatch | null;
  nextStop: Dispatch | null;
  isOff: boolean; // tech is marked off today via pseudo
}

/**
 * Build per-tech lanes. Always creates a lane for every active tech, even
 * those with zero dispatches that day (so dispatchers can drag-drop new
 * work onto them).
 *
 * If activeTechs is omitted/null, falls back to only showing techs who
 * have at least one dispatch (legacy behavior).
 */
export function groupByTech(
  rows: Dispatch[],
  activeTechs?: ActiveTech[] | null,
): TechLane[] {
  const stopsByTech = new Map<string, Dispatch[]>();
  for (const r of rows) {
    if (!stopsByTech.has(r.tech_empno)) stopsByTech.set(r.tech_empno, []);
    stopsByTech.get(r.tech_empno)!.push(r);
  }

  // Build the union of "techs to show": active roster + any tech with stops
  const empnosToShow = new Set<string>();
  if (activeTechs) {
    for (const t of activeTechs) empnosToShow.add(t.empno);
  }
  for (const empno of stopsByTech.keys()) empnosToShow.add(empno);

  const techNameMap = new Map<string, string>();
  if (activeTechs) {
    for (const t of activeTechs) {
      techNameMap.set(
        t.empno,
        t.empname ??
          `${t.firstname ?? ""} ${t.lastname ?? ""}`.trim() ??
          t.empno,
      );
    }
  }

  const lanes: TechLane[] = [];
  for (const empno of empnosToShow) {
    const stops = (stopsByTech.get(empno) ?? []).slice();
    stops.sort((a, b) =>
      (a.tpromtime ?? "").localeCompare(b.tpromtime ?? ""),
    );

    // Resolve the tech's display name - prefer the active-techs roster,
    // fall back to the name embedded on any dispatch, then to empno.
    let name = techNameMap.get(empno);
    if (!name && stops.length > 0) {
      name =
        stops[0].tech_name ??
        `${stops[0].tech_first ?? ""} ${stops[0].tech_last ?? ""}`.trim() ??
        empno;
    }
    if (!name) name = empno;

    const customerStops = stops.filter((s) => !isPseudoDispatch(s));
    const doneCount = customerStops.filter((s) =>
      STATUS_DONE.has(s.status ?? ""),
    ).length;
    const activeStop =
      stops.find((s) => STATUS_ACTIVE.has(s.status ?? "")) ?? null;
    const nextStop =
      customerStops.find(
        (s) => STATUS_PENDING.has(s.status ?? "") || !s.status,
      ) ?? null;
    const isOff =
      customerStops.length === 0 &&
      stops.some((s) => detectPseudoKind(s) === "off");

    lanes.push({
      empno,
      name,
      stops,
      totalStops: customerStops.length,
      doneCount,
      activeStop,
      nextStop,
      isOff,
    });
  }

  lanes.sort((a, b) => a.name.localeCompare(b.name));
  return lanes;
}

/**
 * Visual color theme for a dispatch block based on its window/priority code.
 * Matches ESC's actual color scheme from the priority config:
 *   8AM    = Deep Pink                10-2   = Indian Red
 *   8-12   = Light Pink               12-4   = Teal
 *   3-7    = Steel Blue               AFTER  = Green Yellow (after-hours)
 *   INSTAL = Lawn Green               LONG   = Black
 *   OFF    = Orchid (legacy - going  PAIR   = Maroon (paired calls,
 *            away in Pegasus)                  must stay together)
 *   PART   = Orange Red               PLUMB  = Sky Blue (plumbing)
 *   QUOTE  = Dark Violet              REBATE = Light Slate Gray (return
 *   TECH   = Gold                              startup/rebate verification)
 *   blank  = Pale Green (default)
 *
 * `dark` flag means the block bg is too dark for default text colors,
 * so the DispatchBlock should swap to light text everywhere.
 */
// Window/priority colors now live in the single source `@/lib/window`
// (loud board form + calm dot). windowColorClasses is kept as the board's
// existing entry point and delegates there — the board's look is unchanged,
// and there's exactly one place that knows "12-4 is teal". See DS-04.
export type { WindowColors } from "@/lib/window";
export { windowColors as windowColorClasses } from "@/lib/window";

// --- BOARD-NOTES-EDIT-01: Pegasus-native dispatch notes (additive layer) ---
// These read/write `dispatch_note` (Supabase, RLS) — Pegasus's OWN note for a
// dispatch. ESC's notes stay read-only (DEC-22/DEC-25); this becomes the note
// at dispatch cutover. One editable note per dispatch (upsert by number).

export interface DispatchNote {
  dispatch: string;
  note: string;
  updated_by: string | null;
  updated_at: string | null;
}

export function useDispatchNote(dispatch: string | null | undefined) {
  return useQuery({
    queryKey: ["dispatch_note", dispatch],
    enabled: Boolean(dispatch),
    queryFn: async (): Promise<DispatchNote | null> => {
      const { data, error } = await supabase
        .from("dispatch_note")
        .select("dispatch, note, updated_by, updated_at")
        .eq("dispatch", dispatch as string)
        .maybeSingle();
      if (error) throw error;
      return (data as DispatchNote | null) ?? null;
    },
    staleTime: 30 * 1000,
  });
}

export function useSaveDispatchNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      dispatch: string;
      custno?: string | null;
      locno?: string | null;
      note: string;
    }) => {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email ?? null;
      const { error } = await supabase.from("dispatch_note").upsert(
        {
          dispatch: vars.dispatch,
          custno: vars.custno ?? null,
          locno: vars.locno ?? null,
          note: vars.note,
          updated_by: email,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "dispatch" },
      );
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["dispatch_note", vars.dispatch] });
    },
  });
}

// --- BUILD-20a: route plan (the dispatcher's chosen stop order per tech/day) ---

export interface RoutePlanItem {
  d: string; // dispatch number
  pin: boolean;
}

export function useRoutePlan(
  date: string | null | undefined,
  tech: string | null | undefined,
) {
  return useQuery({
    queryKey: ["route_plan", date, tech],
    enabled: Boolean(date && tech),
    queryFn: async (): Promise<RoutePlanItem[] | null> => {
      const { data, error } = await supabase
        .from("route_plan")
        .select("sequence")
        .eq("appointment_date", date as string)
        .eq("tech_empno", tech as string)
        .maybeSingle();
      if (error) throw error;
      return (data?.sequence as RoutePlanItem[] | undefined) ?? null;
    },
    staleTime: 15 * 1000,
  });
}

export function useSaveRoutePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      date: string;
      tech: string;
      sequence: RoutePlanItem[];
    }) => {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email ?? null;
      const { error } = await supabase.from("route_plan").upsert(
        {
          appointment_date: vars.date,
          tech_empno: vars.tech,
          sequence: vars.sequence,
          updated_by: email,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "appointment_date,tech_empno" },
      );
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["route_plan", vars.date, vars.tech] });
    },
  });
}

// --- BUILD-20b: cross-tech assignment override (move a call to another tech) ---
// Pegasus-native; never writes ESC. The board remaps tech_empno through these.

export function useDispatchAssignments() {
  return useQuery({
    queryKey: ["dispatch_assignments"],
    queryFn: async (): Promise<Record<string, string>> => {
      const { data, error } = await supabase
        .from("dispatch_assignment")
        .select("dispatch, tech_empno");
      if (error) throw error;
      const map: Record<string, string> = {};
      for (const r of (data ?? []) as {
        dispatch: string;
        tech_empno: string;
      }[]) {
        map[r.dispatch] = r.tech_empno;
      }
      return map;
    },
    staleTime: 15 * 1000,
  });
}

export function useSetAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { dispatch: string; tech: string }) => {
      const { data: userData } = await supabase.auth.getUser();
      const email = userData?.user?.email ?? null;
      const { error } = await supabase.from("dispatch_assignment").upsert(
        {
          dispatch: vars.dispatch,
          tech_empno: vars.tech,
          updated_by: email,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "dispatch" },
      );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dispatch_assignments"] });
    },
  });
}

/**
 * Tech seniority — DERIVED + transparent (PREF-FINDER, Seth 2026-06-23): a tech is
 * "senior" if tenure ≥ 5 yrs (mirror_employee.hire_date) OR ≥ 10 logged skills.
 * Shared by the schedule finder + the whole-day optimizer. Keyed ["tech_seniority"].
 */
export function useTechSeniority() {
  return useQuery({
    queryKey: ["tech_seniority"],
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const [{ data: emps }, { data: skills }] = await Promise.all([
        supabase.from("mirror_employee").select("empno, hire_date"),
        supabase.from("tech_skill").select("empno"),
      ]);
      const skillCount = new Map<string, number>();
      for (const s of (skills ?? []) as { empno: string }[])
        skillCount.set(s.empno, (skillCount.get(s.empno) ?? 0) + 1);
      const now = Date.now();
      const m = new Map<string, { senior: boolean; tenure: number; skills: number }>();
      for (const e of (emps ?? []) as { empno: string; hire_date: string | null }[]) {
        const sk = skillCount.get(e.empno) ?? 0;
        const tenure = e.hire_date
          ? (now - new Date(e.hire_date).getTime()) / (365.25 * 86400000)
          : 0;
        m.set(e.empno, { senior: tenure >= 5 || sk >= 10, tenure, skills: sk });
      }
      return m;
    },
  });
}

// --- Tech skills (who's flagged capable of which skill_domain) — for the
// recommendation engine's qualification check. Loaded from the Technician
// Skillsets baseline; see research/tech-skills.md. ---

// --- Location notes (ESC `mirror_location.notes`) for the day's customers, so
// the recommendation engine can read customer-stated tech requests ("Miles
// Only" / "prefers Miles") that live on the LOCATION, not the dispatch row.
// Keyed by `custno|locno` (locno is unique only within a customer). ---

export function useLocationNotes(custnos: string[]) {
  const key = [...new Set(custnos)].filter(Boolean).sort();
  return useQuery({
    queryKey: ["location_notes_board", key],
    enabled: key.length > 0,
    queryFn: async (): Promise<Record<string, string>> => {
      const map: Record<string, string> = {};
      const chunk = 200;
      for (let i = 0; i < key.length; i += chunk) {
        const slice = key.slice(i, i + chunk);
        const { data, error } = await supabase
          .from("mirror_location")
          .select("custno, locno, notes")
          .in("custno", slice);
        if (error) throw error;
        for (const r of (data ?? []) as {
          custno: string;
          locno: string;
          notes: string | null;
        }[]) {
          if (r.notes) map[`${r.custno}|${r.locno}`] = r.notes;
        }
      }
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useTechSkills() {
  return useQuery({
    queryKey: ["tech_skills"],
    queryFn: async (): Promise<Record<string, number[]>> => {
      const { data, error } = await supabase
        .from("tech_skill")
        .select("empno, skill_domain_id")
        .eq("admin_flagged_capable", true);
      if (error) throw error;
      const map: Record<string, number[]> = {};
      for (const r of (data ?? []) as {
        empno: string;
        skill_domain_id: number;
      }[]) {
        (map[r.empno] ??= []).push(r.skill_domain_id);
      }
      return map;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * BOARD-HD (Seth 2026-09-01 board pass): every ACTIVE maintenance-plan
 * customer, as one cached Set — so board cards can wear the gold PLUS chip
 * the same way the mobile on-call list does. One query for the whole board
 * (react-query dedupes the per-card calls onto this single cache entry).
 */
export function usePlusAgreements() {
  return useQuery({
    queryKey: ["board_plus_custnos"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<Set<string>> => {
      const today = new Date().toLocaleDateString("en-CA", {
        timeZone: "America/Chicago",
      });
      const { data, error } = await supabase
        .from("mirror_serv_agr")
        .select("custno")
        .eq("sa_inactive", 0)
        .gte("expire_date", today)
        .limit(20000);
      if (error) throw error;
      return new Set(
        ((data ?? []) as { custno: string }[]).map((r) => r.custno),
      );
    },
  });
}
